<div class="w-full max-w-full box-border p-4 bg-gray-50 overflow-visible h-auto touch-pan-y font-sans">
  <!-- Loading State -->
  @if (loading()) {
    <div class="p-12 text-center bg-white rounded-lg shadow-md my-8 mx-auto max-w-[600px]">
      <div class="w-12 h-12 border-4 border-blue-500/20 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
      <p class="text-gray-600 italic">{{ 'portal.mydata.loading' | translate }}</p>
    </div>
  }

  <!-- Error State -->
  @if (error() && !loading()) {
    <div class="p-12 text-center bg-white rounded-lg shadow-md my-8 mx-auto max-w-[600px]">
      <div class="text-5xl mb-4">⚠️</div>
      <p class="text-red-500 text-lg mb-4 italic">{{ error() }}</p>
      <button class="px-8 py-3 bg-gradient-to-br from-indigo-500 to-purple-600 text-white border-0 rounded-lg text-base font-semibold cursor-pointer transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5" 
              (click)="navigateBack()" type="button">
        {{ 'portal.mydata.back' | translate }}
      </button>
    </div>
  }

  <!-- Contact Details -->
  @if (!loading() && !error() && contact()) {
    <!-- Two Column Layout: Personal Info + Addresses -->
    <div class="grid grid-cols-1 2xl:grid-cols-2 gap-6 w-full max-w-full box-border items-start">
      <!-- Left Column: Personal Information Card -->
      <div class="h-auto w-full min-w-0 max-w-full box-border flex flex-col bg-white rounded-lg shadow-md touch-pan-y">
        <div class="p-6 h-auto min-h-0">
          <div class="flex flex-col xl:flex-row gap-6 items-stretch min-h-0">
            <!-- Photo Column -->
            <div class="flex flex-col justify-start items-start shrink-0 gap-3 w-full xl:w-[220px] xl:min-w-[220px]">
              <!-- Contact Photo Component -->
              <app-contact-photo
                [contactId]="contact()!.contact_id"
                size="large"
                [showFallbackText]="true"
                [fallbackText]="'portal.mydata.photo.nophoto' | translate"
                [enableUpload]="true"
                [showDeleteButton]="true"
                (photoChanged)="onPhotoChanged()"
                class="w-full"
              />

              <!-- Contact IDs section -->
              <div class="flex flex-col w-auto max-w-full bg-gray-50 border border-gray-200 rounded p-2 gap-1 xl:flex-1 xl:min-w-[200px]">
                <div class="flex flex-col gap-0.5">
                  <label class="font-semibold text-[0.75rem] text-gray-500 uppercase tracking-wider">{{ 'portal.mydata.contactid' | translate }}</label>
                  <span class="font-medium text-gray-800 text-[0.875rem]">{{ contact()!.contact_id }}</span>
                </div>
              </div>
            </div>

            <!-- Info Details Container -->
            <div class="flex flex-col flex-1 gap-6 min-w-0 min-h-0">

              <!-- Name Header with Status -->
              <div class="mb-2">
                <div class="flex items-center gap-3 flex-wrap text-green-700">
                  <span class="text-2xl text-green-500">✓</span>
                  <span class="text-2xl font-bold leading-tight">
                    {{ contact()!.prename || '-' }}
                    {{ contact()!.lastname || '-' }}
                  </span>
                  @if (contact()!.gender) {
                    <span class="ml-2 text-[20px]" 
                          [class.text-blue-500]="contact()!.gender === 'M' || contact()!.gender === 'Herr'"
                          [class.text-pink-500]="contact()!.gender === 'F' || contact()!.gender === 'Frau'"
                          [class.text-purple-500]="contact()!.gender === 'N' || contact()!.gender === 'Divers'">
                      {{ getGenderIcon(contact()!.gender!) }}
                    </span>
                  }
                </div>
              </div>

              <!-- Unified Contact Information Card -->
              <div class="relative bg-gray-50/90 border border-gray-200 rounded-lg p-4 flex flex-col gap-4 w-full box-border">
                <!-- Communication Group -->
                <div class="flex flex-col gap-1.5 pl-3 relative before:content-[''] before:absolute before:left-0 before:top-0 before:bottom-0 before:w-1 before:bg-orange-500 before:rounded-sm">
                  <div class="text-[1rem] text-gray-800 leading-relaxed break-words">{{ userEmail() || '-' }}</div>
                  <div class="text-[1rem] text-gray-800 leading-relaxed break-words">{{ contact()!.phone || '-' }}</div>
                </div>

                <!-- Personal Information Group -->
                <div class="flex flex-col gap-4 pl-3 relative before:content-[''] before:absolute before:left-0 before:top-0 before:bottom-0 before:w-1 before:bg-purple-600 before:rounded-sm">
                  <div class="flex flex-col gap-1">
                    <label class="font-semibold text-[0.875rem] text-gray-500 uppercase tracking-wider">{{ 'portal.mydata.birthday' | translate }}</label>
                    <span class="text-[1rem] text-gray-800 leading-relaxed">{{ contact()!.birthday ? formatDate(contact()!.birthday!) : '-' }}</span>
                  </div>
                  <div class="flex flex-col gap-1">
                    <label class="font-semibold text-[0.875rem] text-gray-500 uppercase tracking-wider">{{ 'portal.mydata.ahv' | translate }}</label>
                    <span class="text-[1rem] text-gray-800 leading-relaxed">{{ contact()!.ahv || '-' }}</span>
                  </div>
                  <div class="flex flex-col gap-1">
                    <label class="font-semibold text-[0.875rem] text-gray-500 uppercase tracking-wider">{{ 'portal.mydata.hometown' | translate }}</label>
                    <span class="text-[1rem] text-gray-800 leading-relaxed">{{ contact()!.hometown || '-' }}</span>
                  </div>
                  <div class="flex flex-col gap-1">
                    <label class="font-semibold text-[0.875rem] text-gray-500 uppercase tracking-wider">{{ 'portal.mydata.nationality' | translate }}</label>
                    <span class="text-[1rem] text-gray-800 leading-relaxed">{{ getCountryName(contact()?.nationality ?? null) }}</span>
                  </div>
                </div>

                <!-- Edit Button Container -->
                @if (!roleService.isLecturer()) {
                  <div class="absolute bottom-4 right-4 z-10 xl:relative xl:bottom-auto xl:right-auto xl:flex xl:justify-end xl:-order-1 xl:mb-2">
                    <button type="button" (click)="toggleEditMode()"
                            class="bg-gradient-to-br from-indigo-500 to-purple-600 text-white border-0 px-6 py-2.5 rounded-lg font-semibold text-[0.95rem] cursor-pointer transition-all flex items-center gap-2 hover:shadow-lg hover:-translate-y-0.5 xl:w-full xl:justify-center">
                      <span class="text-[1.1rem]">✏️</span>
                      {{ 'portal.mydata.edit' | translate }}
                    </button>
                  </div>
                }
              </div>

              <!-- Lecturer HR Portal Note -->
              @if (roleService.isLecturer()) {
                <div class="flex items-start gap-2 mt-4 animate-fade-in group">
                  <span class="material-symbols-outlined text-amber-500 text-xl select-none mt-0.5">warning</span>
                  <div class="flex flex-col">
                    <span class="text-sm text-gray-500 italic font-normal">{{ 'portal.mydata.contact.hr_portal_notice' | translate }}</span>
                    <a href="https://vantage.requests.eu.people-doc.com/employee/login/?normal_access" 
                       target="_blank" 
                       rel="noopener noreferrer"
                       class="text-sm text-blue-600 hover:text-blue-800 underline font-medium mt-1 break-all">
                      https://vantage.requests.eu.people-doc.com/employee/login/?normal_access
                    </a>
                  </div>
                </div>
              }

              <!-- User Attributes Section (Integrated) -->
              @if (contact() && contact()!.contact_id) {
                <div class="w-full mt-6 box-border border-t border-gray-200 pt-6">
                  <app-user-attributes [contactId]="contact()!.contact_id" />
                </div>
              }
            </div>
          </div>
        </div>
      </div>

      <!-- Right Column: Address Management Section -->
      @if (contact() && contact()!.contact_id) {
        <div class="w-full min-w-0 max-w-full h-auto bg-white rounded-lg shadow-md p-6 box-border overflow-visible touch-pan-y">
          <app-address-list
            [contactId]="contact()!.contact_id"
            [initialAddresses]="loadedAddresses()"
          />
        </div>
      }
    </div>
  }
</div>
